import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import { createStackNavigator } from "@react-navigation/stack";
import React from "react";
import { LayoutAnimation, TouchableOpacity, View } from "react-native";
import {
	CLEAR_PUBLISH,
	CLEAR_TEMPLATE,
	LOGOUT,
	RESET_POST,
	TOGGLE_DISPLAY_PREVIEW
} from "../actions/types";
import LoginModal from "../components/LoginModal";
import { BUSINESS_NAME } from "../config/constants";
import AdDetails from "../containers/AdDetails";
import AppInfos from "../containers/AppInfos";
import CameraScreen from "../containers/CameraScreen";
import CGUReader from "../containers/CGUReader";
import Chat from "../containers/Chat";
import CitiesModal from "../containers/CitiesModal";
import CommunitiesMapModal from "../containers/CommunitiesMapModal";
import CommunitiesModal from "../containers/CommunitiesModal";
import CommunityDetails from "../containers/CommunityDetails";
import PdfWelcomeScreen from "../containers/PdfWelcomeScreen";
import DocReader from "../containers/DocReader";
import EventDetails from "../containers/EventDetails";
import EventInterestedUsers from "../containers/EventInterestedUsers";
import ImageZoom from "../containers/ImgZoom";
import LandingScreen from "../containers/LandingScreen";
import LoadingScreen from "../containers/LoadingScreen";
import MaintenanceScreen from "../containers/Maintenance";
import Post from "../containers/Post";
import CreateTemplate from "../containers/CreateTemplate";
import Profile from "../containers/Profile";
import ProfileUpdate from "../containers/ProfileUpdate";
import ResetPassword from "../containers/ResetPassword";
import SelectTemplate from "../containers/SelectTemplate";
import SelectTemplateDate from "../containers/SelectTemplateDate";
import Youtube from "../containers/Youtube";
import { t } from "../services/i18n";
import commonStyles, {
	COLOR2,
	COLOR5,
	WHITE_COLOR
} from "../styles/commonStyles";
import BottomTabNavigator from "./BottomTabsNavigator";
import {
	DEFAULT_HEADER_STYLE,
	getCommunityUri,
	getHeaderLeftBack
} from "./CommonNavigation";

const Stack = createStackNavigator();

function getTemplateHeaderStyle(displayPreview) {
	if (displayPreview) {
		return {
			backgroundColor: COLOR5
		};
	}
	return DEFAULT_HEADER_STYLE;
}

function getTemplateHeaderRight(dispatch, displayPreview, keyboardOpen) {
	const { bgWhite, bgColor2, justifyContentCenter, alignItemsCenter } =
		commonStyles;

	return () => (
		<TouchableOpacity
			style={[
				displayPreview ? bgWhite : bgColor2,
				alignItemsCenter,
				justifyContentCenter,
				{
					// `@react-navigation/elements` v2 lays the header slots out as
					// rows, so `flex: 1` here would size the button off the free
					// width instead of the intended 56pt block, and `height: "100%"`
					// no longer resolves. Stretch instead.
					alignSelf: "stretch",
					width: 56,
					paddingBottom: 3
				}
			]}
			onPress={() => {
				LayoutAnimation.easeInEaseOut();
				dispatch({
					type: TOGGLE_DISPLAY_PREVIEW
				});
			}}
		>
			{keyboardOpen ? (
				<MaterialIcons
					name="keyboard-hide"
					color={displayPreview ? COLOR2 : WHITE_COLOR}
					size={28}
				/>
			) : (
				<MaterialCommunityIcons
					name="eye-check"
					color={displayPreview ? COLOR2 : WHITE_COLOR}
					size={28}
				/>
			)}
		</TouchableOpacity>
	);
}

const config = {
	animation: "timing",
	config: {
		duration: 500
	}
};

export const createRootAppStackNavigator = ({
	dispatch,
	user,
	accessDenied,
	navigation,
	displayPreview,
	keyboardOpen,
	communityDetails,
	isFetching
}) => {
	return (
		<Stack.Navigator
			screenOptions={{
				title: BUSINESS_NAME,
				// v7 makes the modal presentation contagious: `getModalRouteKeys`
				// marks every route that follows a modal one and does not set
				// `presentation` itself, so ordinary screens pushed after the modal
				// group below started sliding up from the bottom instead of in from
				// the right. Stating the default explicitly stops the propagation.
				presentation: "card",
				headerTitleAlign: "left",
				headerStyle: DEFAULT_HEADER_STYLE,
				headerTintColor: WHITE_COLOR,
				// `@react-navigation/elements` v2 insets the header content by 5pt
				// on iOS devices at least 414pt wide (v1 did not). Every screen with
				// a visible header here uses a full-bleed coloured button (the menu
				// and filter on the tabs, `getHeaderLeftBack` elsewhere), so they all
				// need it cancelled to reach the screen edges. The app is locked to
				// portrait, so the left/right safe-area insets this overrides are
				// always 0.
				headerLeftContainerStyle: { marginStart: -5 },
				headerRightContainerStyle: { marginEnd: -5 },
				// v2 drops the title's leading margin from 16pt to 4pt as soon as
				// the title is left-aligned and a left button is present.
				headerTitleContainerStyle: { marginStart: 16 },
				headerTitleStyle: {
					fontFamily: "fontBold",
					textTransform: "uppercase"
				}
			}}
		>
			{Boolean(!user || isFetching) ? (
				!accessDenied ? (
					<>
						<Stack.Group>
							<Stack.Screen
								name="LoadingScreen"
								component={LoadingScreen}
								options={{ headerShown: false }}
							/>
							<Stack.Screen
								name="MaintenanceScreen"
								component={MaintenanceScreen}
								options={{ headerShown: false }}
							/>
						</Stack.Group>
					</>
				) : (
					<>
						<Stack.Group>
							<Stack.Screen
								name="LandingScreen"
								component={LandingScreen}
								initialParams={{
									communityUri: getCommunityUri(communityDetails)
								}}
								options={{ headerShown: false }}
							/>
							<Stack.Screen
								name="ResetPasswordScreen"
								component={ResetPassword}
								initialParams={{
									communityUri: getCommunityUri(communityDetails)
								}}
								options={{
									title: t("resetpwd:title"),
									headerLeft: getHeaderLeftBack(navigation)
								}}
							/>
							<Stack.Screen
								name="AppInfosScreen"
								component={AppInfos}
								options={{
									title: t("menu:about"),
									headerLeft: getHeaderLeftBack(navigation)
								}}
							/>
							<Stack.Screen
								name="CGUScreen"
								component={CGUReader}
								options={{
									title: t("menu:cgu"),
									headerLeft: getHeaderLeftBack(navigation)
								}}
							/>
							<Stack.Screen
								name="AdScreen"
								component={AdDetails}
								options={() => {
									return {
										title: t("menu:localLife"),
										headerLeft: getHeaderLeftBack(navigation)
									};
								}}
							/>
							<Stack.Screen
								name="SelectTemplateDateScreen"
								component={SelectTemplateDate}
								options={({ route }) => {
									return {
										headerTitleStyle: {
											textTransform: "none"
										},
										headerLeft: getHeaderLeftBack(navigation, {
											callback: () => dispatch({ type: CLEAR_PUBLISH })
										}),
										headerRight: getTemplateHeaderRight(
											dispatch,
											displayPreview,
											keyboardOpen
										),
										headerTitleStyle: {
											textTransform: "none"
										},
										headerStyle: getTemplateHeaderStyle(displayPreview)
									};
								}}
							/>
							<Stack.Screen
								name="CreateTemplateScreen"
								component={CreateTemplate}
								options={({ route }) => {
									return {
										headerTitleStyle: {
											textTransform: "none"
										},
										headerLeft: getHeaderLeftBack(navigation, {
											callback: () => dispatch({ type: CLEAR_PUBLISH })
										}),
										headerRight: getTemplateHeaderRight(
											dispatch,
											displayPreview,
											keyboardOpen
										),
										headerTitleStyle: {
											textTransform: "none"
										},
										headerStyle: getTemplateHeaderStyle(displayPreview)
									};
								}}
							/>
							<Stack.Screen
								name="EventDetailsScreen"
								component={EventDetails}
								options={{
									title: t("menu:post"),
									headerLeft: getHeaderLeftBack(navigation)
								}}
							/>
							<Stack.Screen
								name="CommunityDetailsScreen"
								component={CommunityDetails}
								initialParams={{
									communityUri: getCommunityUri(communityDetails)
								}}
								options={{
									title: t("menu:camping"),
									headerLeft: getHeaderLeftBack(navigation)
								}}
							/>
							<Stack.Screen
								name="ImageZoomScreen"
								component={ImageZoom}
								options={{
									title: t("menu:zoom"),
									headerLeft: getHeaderLeftBack(navigation)
								}}
							/>
							<Stack.Screen
								name="DocReaderScreen"
								component={DocReader}
								options={{
									title: t("button:pdfdocument"),
									headerLeft: getHeaderLeftBack(navigation)
								}}
							/>
							<Stack.Screen
								name="MaintenanceScreen"
								component={MaintenanceScreen}
								options={{ headerShown: false }}
							/>
						</Stack.Group>
						<Stack.Group
							screenOptions={{
								presentation: "modal",
								headerShown: false,
								animationEnabled: true,
								cardStyle: {
									backgroundColor: "gray"
									// opacity: 0.9
								},
								transitionConfig: () => ({
									containerStyle: {
										backgroundColor: "transparent"
									}
								}),
								transitionSpec: {
									open: config,
									close: config
								}
							}}
						>
							<Stack.Screen
								name="CommunitiesModal"
								component={CommunitiesModal}
							/>
							<Stack.Screen
								name="CommunitiesMapModal"
								component={CommunitiesMapModal}
							/>
							<Stack.Screen name="CitiesModal" component={CitiesModal} />
							<Stack.Screen
								name="LoginModal"
								component={LoginModal}
								initialParams={{
									communityUri: getCommunityUri(communityDetails)
								}}
							/>
						</Stack.Group>
					</>
				)
			) : user.needUpdate ? (
				<>
					<Stack.Screen
						name="ProfileUpdateScreen"
						component={ProfileUpdate}
						initialParams={{
							communityUri: getCommunityUri(communityDetails)
						}}
						options={{
							title: t("menu:profile"),
							headerLeft: getHeaderLeftBack(navigation, {
								callback: () =>
									dispatch({
										type: LOGOUT
									})
							})
						}}
					/>
					<Stack.Screen
						name="CameraScreen"
						component={CameraScreen}
						options={{ headerShown: false }}
					/>
				</>
			) : (
				<>
					<Stack.Screen name="TabScreen" component={BottomTabNavigator} />
					<Stack.Screen
						name="ProfileUpdateScreen"
						component={ProfileUpdate}
						options={{
							title: t("menu:profile"),
							headerLeft: getHeaderLeftBack(navigation, {
								fallback: "TabScreen"
							})
						}}
					/>
					<Stack.Screen
						name="CameraScreen"
						component={CameraScreen}
						options={{ headerShown: false }}
					/>
					<Stack.Screen
						name="SelectActivityScreen"
						component={SelectTemplate.SelectActivity}
						initialParams={{
							communityUri: getCommunityUri(communityDetails)
						}}
						options={{
							title: t("menu:instantsharing"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="SelectProposalScreen"
						component={SelectTemplate.SelectProposal}
						initialParams={{
							communityUri: getCommunityUri(communityDetails)
						}}
						options={{
							title: t("menu:needservice"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="SelectTemplateScreen"
						component={SelectTemplate.SelectTemplate}
						initialParams={{
							communityUri: getCommunityUri(communityDetails)
						}}
						options={{
							title: t("menu:templates"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="CreateTemplateScreen"
						component={CreateTemplate}
						initialParams={{
							communityUri: getCommunityUri(communityDetails)
						}}
						options={({ route }) => {
							return {
								headerLeft: getHeaderLeftBack(navigation, {
									callback: () => dispatch({ type: CLEAR_TEMPLATE })
								}),
								title: t("menu:templates"),
								headerRight: getTemplateHeaderRight(
									dispatch,
									displayPreview,
									keyboardOpen
								),
								headerStyle: getTemplateHeaderStyle(displayPreview)
							};
						}}
					/>
					<Stack.Screen
						name="SelectTemplateDateScreen"
						component={SelectTemplateDate}
						initialParams={{
							communityUri: getCommunityUri(communityDetails)
						}}
						options={({ route }) => {
							return {
								headerTitleStyle: {
									textTransform: "none"
								},
								headerLeft: getHeaderLeftBack(navigation, {
									callback: () => dispatch({ type: CLEAR_PUBLISH })
								}),
								headerRight: getTemplateHeaderRight(
									dispatch,
									displayPreview,
									keyboardOpen
								),
								headerStyle: getTemplateHeaderStyle(displayPreview)
							};
						}}
					/>
					<Stack.Screen
						name="PostScreen"
						component={Post}
						initialParams={{
							communityUri: getCommunityUri(communityDetails)
						}}
						options={{
							title: t("menu:snapshot"),
							headerLeft: getHeaderLeftBack(navigation, {
								callback: () => dispatch({ type: RESET_POST })
							}),
							headerRight: getTemplateHeaderRight(
								dispatch,
								displayPreview,
								keyboardOpen
							),
							headerStyle: getTemplateHeaderStyle(displayPreview)
						}}
					/>
					<Stack.Screen
						name="EventDetailsScreen"
						component={EventDetails}
						options={{
							title: t("menu:post"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="EventInterestedUsersScreen"
						component={EventInterestedUsers}
						initialParams={{
							communityUri: getCommunityUri(communityDetails)
						}}
						options={{
							title: t("menu:post"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="ProfileScreen"
						component={Profile}
						options={{
							title: t("menu:profile"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="ProfileTeamScreen"
						component={Profile}
						options={{
							title: t("menu:profile"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="AdDetailsScreen"
						component={AdDetails}
						options={() => {
							return {
								title: t("menu:localLife"),
								headerLeft: getHeaderLeftBack(navigation)
							};
						}}
					/>
					<Stack.Screen
						name="AdScreen"
						component={AdDetails}
						options={() => {
							return {
								title: t("menu:localLife"),
								headerLeft: getHeaderLeftBack(navigation)
							};
						}}
					/>
					<Stack.Screen
						name="CommunityDetailsScreen"
						component={CommunityDetails}
						initialParams={{
							communityUri: getCommunityUri(communityDetails)
						}}
						options={{
							title: t("menu:neighborhood"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="ChatScreen"
						component={Chat}
						options={() => {
							return {
								headerTitleStyle: {
									textTransform: "none"
								},
								headerLeft: getHeaderLeftBack(navigation, {
									// origin: "ChatTab"
								})
							};
						}}
					/>
					<Stack.Screen
						name="ChatTeamScreen"
						component={Chat}
						options={() => {
							return {
								headerTitleStyle: {
									textTransform: "none"
								},
								headerLeft: getHeaderLeftBack(navigation, {
									// origin: "ChatTab"
								})
							};
						}}
					/>
					<Stack.Screen
						name="ChatGrpScreen"
						component={Chat}
						initialParams={{
							communityUri: getCommunityUri(communityDetails)
						}}
						options={() => {
							return {
								headerLeft: getHeaderLeftBack(navigation),
								headerTitleStyle: {
									textTransform: "none"
								}
							};
						}}
					/>
					<Stack.Screen
						name="ImageZoomScreen"
						component={ImageZoom}
						options={{
							title: t("menu:zoom"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="YoutubeScreen"
						component={Youtube}
						options={{
							title: t("menu:youtube"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="AppInfosScreen"
						component={AppInfos}
						options={{
							title: t("menu:about"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="CGUScreen"
						component={CGUReader}
						options={{
							title: t("menu:cgu"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="PdfWelcomeScreen"
						initialParams={{ communityUri: communityDetails?.customUri }}
						component={PdfWelcomeScreen}
						options={{
							title: t("menu:welcomepdf"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="DocReaderScreen"
						component={DocReader}
						options={{
							title: t("menu:PDF"),
							headerLeft: getHeaderLeftBack(navigation)
						}}
					/>
					<Stack.Screen
						name="MaintenanceScreen"
						component={MaintenanceScreen}
						options={{ headerShown: false }}
					/>
				</>
			)}
		</Stack.Navigator>
	);
};
